<template>
  <!-- An authenticator-app code (AXA): only while the read is on the code
       screen, and nothing about mailboxes. The code changes every 30 seconds,
       so there is nothing to type ahead of the question. -->
  <div
    v-if="authenticator && asking"
    class="twofa-card twofa-card--urgent"
  >
    <span class="twofa-card__key">🔑</span>

    <div class="twofa-card__main">
      <p class="twofa-card__title">
        {{ $t('carriers.import.twoFa.Title', { carrier: carrierName }) }}
      </p>

      <p class="twofa-card__body">
        {{ $t('carriers.import.twoFa.AuthenticatorBody', { carrier: carrierName }) }}
        <span v-if="accountLabel" class="twofa-card__offer-account">· {{ accountLabel }}</span>
      </p>

      <p v-if="rejected" class="twofa-card__rejected">
        {{ $t('carriers.import.twoFa.AuthenticatorRejected') }}
      </p>
      <p v-else-if="checking" class="twofa-card__watching">
        <span class="twofa-card__dot"></span>
        {{ $t('carriers.import.twoFa.AuthenticatorChecking', { carrier: carrierName }) }}
      </p>

      <div class="twofa-card__row">
        <el-input
          ref="input"
          v-model="code"
          class="twofa-card__input"
          inputmode="numeric"
          pattern="[0-9]*"
          autocomplete="one-time-code"
          placeholder="______"
          :disabled="submitting"
          @input="onInput"
          @keyup.enter.native="submit"
        />
        <BaseButton
          :loading="submitting"
          :disabled="!valid"
          @click="submit"
        >
          {{ $t('Integrations.allianz.forwarding.ManualCodeSubmit') }}
        </BaseButton>
      </div>

      <p class="twofa-card__note">{{ $t('carriers.import.twoFa.AuthenticatorValidity') }}</p>
    </div>
  </div>

  <!-- Two states, and only one of them is a banner. A run that is reading
       happily gets a line; a run actually sitting on the code screen gets the
       whole amber card. -->
  <div
    v-else-if="asking"
    class="twofa-card twofa-card--urgent"
  >
    <span class="twofa-card__key">🔑</span>

    <div class="twofa-card__main">
      <p class="twofa-card__title">
        {{ $t('carriers.import.twoFa.Title', { carrier: carrierName }) }}
      </p>

      <p class="twofa-card__body">
        {{ $t('carriers.import.twoFa.Body', { account: accountLabel }) }}
      </p>

      <p v-if="account.twoFaEmail" class="twofa-card__watching">
        <span class="twofa-card__dot"></span>
        {{ $t('carriers.import.twoFa.Watching', { mailbox: carrierImportReceiverAddress }) }}
      </p>
      <p v-else class="twofa-card__watching twofa-card__watching--manual">
        {{ $t('carriers.import.twoFa.NoForwarding') }}
      </p>

      <div class="twofa-card__row">
        <el-input
          v-model="code"
          class="twofa-card__input"
          maxlength="8"
          placeholder="______"
          @input="onInput"
          @keyup.enter.native="submit"
        />
        <BaseButton
          :loading="submitting"
          :disabled="!valid"
          @click="submit"
        >
          {{ $t('Integrations.allianz.forwarding.ManualCodeSubmit') }}
        </BaseButton>
      </div>

      <p class="twofa-card__note">{{ $t('carriers.import.twoFa.Validity') }}</p>
    </div>
  </div>

  <!-- Nobody has asked for a code. The field stays reachable, because the
       worker's report is fire-and-forget and its absence is not proof — but as
       an offer, not an alarm. -->
  <div v-else-if="!authenticator" class="twofa-card twofa-card--quiet">
    <button
      class="twofa-card__offer"
      type="button"
      @click="open = !open"
    >
      <span class="twofa-card__offer-key">🔑</span>
      {{ $t('carriers.import.twoFa.EnterCode', { carrier: carrierName }) }}
      <span v-if="accountLabel" class="twofa-card__offer-account">{{ accountLabel }}</span>
    </button>

    <div v-if="open" class="twofa-card__opened">
      <p v-if="!account.twoFaEmail" class="twofa-card__watching twofa-card__watching--manual">
        {{ $t('carriers.import.twoFa.NoForwardingMaybe') }}
      </p>

      <div class="twofa-card__row">
        <el-input
          v-model="code"
          class="twofa-card__input"
          maxlength="8"
          placeholder="______"
          @input="onInput"
          @keyup.enter.native="submit"
        />
        <BaseButton
          :loading="submitting"
          :disabled="!valid"
          @click="submit"
        >
          {{ $t('Integrations.allianz.forwarding.ManualCodeSubmit') }}
        </BaseButton>
      </div>

      <p class="twofa-card__note">{{ $t('carriers.import.twoFa.Validity') }}</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import { apiError } from '@/utils/carrierImport';

/**
 * The last authenticator code sent per run: the waitingTwoFaAt it answered and when it went.
 * Outside the component because the card is unmounted whenever the run stops waiting, and a
 * rejected code is exactly a run that stopped waiting and then started again.
 */
const sentCodes = {};

/**
 * How long after a code went a new request from the worker still reads as that code's rejection,
 * rather than an unrelated sign-in later in the same run.
 */
const REJECTION_WINDOW_MS = 3 * 60 * 1000;

/**
 * Hand a portal's one-time sign-in code to a run that is waiting for it.
 *
 * On the Carriers page itself, not inside the account's settings window. The
 * code is valid for about five minutes, and the only previous way to type one
 * was five clicks deep behind Edit Carrier → Portal accounts → Settings, which
 * nobody reaches in five minutes unless they already knew it was there.
 *
 * Shown for ANY active run on a login whose portal asks for a code, not only
 * once the worker has reported it is sitting on the code screen. The older
 * Integrations screen learned this the hard way and its comment says why:
 * gating on waitingTwoFaAt leaves a window of exactly one 7s poll to notice the
 * field and type a code the portal honours for ~5 minutes. And that report is
 * fire-and-forget and fail-open, so its absence is not proof either.
 *
 * But absence is not nothing, and a banner must not pretend otherwise. The
 * scraper keeps a stored session per account and skips signing in whenever it
 * is still valid, so most reads never ask for a code at all — on those,
 * waitingTwoFaAt stays null for the whole run, and an amber card claiming "the
 * read is waiting on it" describes a wait nobody is in.
 *
 * So there are two states rather than one card with two wordings. Waiting: the
 * full card, amber, mailbox watch and all. Not waiting: one line offering the
 * field to whoever wants it, folded away until they ask. The field is still
 * reachable in a handful of seconds, and nobody is told to hurry over a code
 * that was never requested.
 *
 * `waitingTwoFaAt` only became trustworthy enough to gate on once the backend
 * started clearing it — it had one writer and no eraser outside the terminal
 * paths, so a run that touched the code screen once kept the stamp, and the
 * card stayed urgent while the progress bar advanced.
 *
 * An authenticator-app portal (AXA) is the exception to all of the above: its
 * code changes every 30 seconds and the worker drops one older than its own
 * request, so there is no offer ahead of time — the card exists only while the
 * read waits. Six digits, sent as soon as the sixth is typed. When the portal
 * rejects one, the worker asks again with a newer waitingTwoFaAt than the one
 * the code answered, and the card says so.
 */
export default {
  name: 'TwoFaCodeCard',
  components: { BaseButton },
  props: {
    /** The account whose run is waiting. */
    account: { type: Object, required: true },
    /** The run itself — its waitingTwoFaAt is what makes this urgent. */
    run: { type: Object, required: true },
    carrierName: { type: String, default: '' },
    /** The portal's second factor: EMAIL (Allianz) or AUTHENTICATOR (AXA). See portalTwoFaKind. */
    twoFaKind: { type: String, default: 'EMAIL' },
  },
  data() {
    return {
      code: '',
      submitting: false,
      open: false,
      /** The authenticator code last sent for this run, kept across remounts. */
      sent: sentCodes[this.run.scrapeJobId] || null,
    };
  },
  computed: {
    ...mapGetters(['carrierImportReceiverAddress']),
    /**
     * The worker has told us it is on the portal's code screen. Null does not
     * mean the opposite — it usually means the run reused its session and will
     * never need a code — so it drives the wording, not the availability.
     */
    asking() {
      return Boolean(this.run.waitingTwoFaAt);
    },
    accountLabel() {
      return this.account.label || this.account.username || '';
    },
    authenticator() {
      return this.twoFaKind === 'AUTHENTICATOR';
    },
    valid() {
      return this.authenticator ? /^\d{6}$/.test(this.code) : /^\d{4,8}$/.test(this.code);
    },
    /** A code went for the question the worker is asking now, and it has not answered yet. */
    checking() {
      return Boolean(this.authenticator && this.sent && this.sent.answered === this.run.waitingTwoFaAt);
    },
    /**
     * The worker asked again after the last code was sent: the portal turned it down. Judged on
     * the server's own stamps (the request after vs. the request the code answered), so the
     * browser's clock only bounds how long ago the code went.
     */
    rejected() {
      const waiting = this.run.waitingTwoFaAt;
      if (!this.authenticator || !this.sent || !waiting || waiting === this.sent.answered) return false;
      if (Date.now() - this.sent.at > REJECTION_WINDOW_MS) return false;
      const now = Date.parse(waiting);
      const before = Date.parse(this.sent.answered);
      return Number.isNaN(now) || Number.isNaN(before) || now > before;
    },
  },
  watch: {
    // A new question from the worker (a rejected code) puts the cursor back in the box.
    'run.waitingTwoFaAt': function () {
      this.focusIfFree();
    },
  },
  mounted() {
    this.focusIfFree();
  },
  methods: {
    /** Into the code box, unless the person is typing somewhere else on the page. */
    focusIfFree() {
      if (!this.authenticator || !this.asking) return;
      this.$nextTick(() => {
        const active = document.activeElement;
        const free = !active || active === document.body || (this.$el && this.$el.contains(active));
        if (free && this.$refs.input) this.$refs.input.focus();
      });
    },
    onInput(v) {
      const digits = (v || '').replace(/\D/g, '');
      // A pasted "123 456" is still six digits; the app shows them with a space.
      this.code = this.authenticator ? digits.slice(0, 6) : digits;
      if (this.authenticator && this.valid) this.submit();
    },
    submit() {
      if (!this.valid || this.submitting) return;
      this.submitting = true;
      const answered = this.run.waitingTwoFaAt;
      axios.post('/insurance/policy/scrape/twofa/submit', {
        scrapeAccountId: this.account.scrapeAccountId,
        code: this.code,
      })
        .then(() => {
          this.code = '';
          if (this.authenticator) {
            this.sent = { answered, at: Date.now() };
            sentCodes[this.run.scrapeJobId] = this.sent;
          }
          this.$notify.success({
            message: this.$t('Integrations.allianz.forwarding.ManualCodeSent'),
          });
          // The worker picks the code up on its own poll; refreshing the runs is
          // what makes this card go away once it has.
          this.$store.dispatch('carrierImportFetchRuns');
        })
        .catch((err) => this.$notify.error({
          message: apiError(err, this.$t('adminTitles.someError')),
        }))
        .finally(() => {
          this.submitting = false;
          this.focusIfFree();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.twofa-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  border: 1px solid var(--solid-lavender-grey);
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 16px;
  background: var(--solid-surface, #fff);

  // The worker is on the code screen right now and the code expires. Amber
  // rather than red: nothing has gone wrong yet.
  &--urgent {
    border-color: var(--solid-orange);
    background: var(--solid-milk-ornage);
  }

  &__key {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    flex: none;
    font-size: 15px;
    background: var(--solid-light-purple);
  }

  &--urgent &__key {
    background: rgba(255, 189, 19, 0.25);
  }

  // Not a banner: no border, no fill, no icon tile. It has to be findable
  // without competing with the run it sits above.
  &--quiet {
    display: block;
    border: 0;
    background: none;
    padding: 0 2px;
    margin-bottom: 12px;
  }

  &__offer {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: none;
    border: 0;
    padding: 2px 0;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: var(--solid-purple);

    &:hover { text-decoration: underline; }
  }

  &__offer-key { font-size: 13px; }

  &__offer-account {
    font-weight: 400;
    color: var(--solid-dark-purple);
  }

  &__opened { margin-top: 10px; max-width: 420px; }

  &__main { min-width: 0; flex: 1; }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: var(--solid-black-purple);
    letter-spacing: -0.015em;
  }

  &__body {
    font-size: 13.5px;
    line-height: 1.55;
    color: var(--solid-black-purple-70);
    margin-top: 5px;
  }

  &__watching {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-top: 8px;

    &--manual { color: var(--solid-black-purple-70); }
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--solid-purple);
    flex: none;
    animation: twoFaPulse 1.4s ease-in-out infinite;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    flex-wrap: wrap;
  }

  // Wide-tracked and centred so six digits read as six digits.
  &__input {
    width: 190px;
    flex: none;

    ::v-deep input {
      font-size: 19px;
      font-weight: 700;
      letter-spacing: 0.22em;
      text-align: center;
    }
  }

  &__rejected {
    font-size: 13px;
    font-weight: 600;
    color: var(--solid-red, #d93025);
    margin-top: 8px;
  }

  &__note {
    font-size: 12px;
    color: var(--solid-dark-purple);
    margin-top: 8px;
  }
}

@keyframes twoFaPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
}

@media (prefers-reduced-motion: reduce) {
  .twofa-card__dot { animation: none; }
}
</style>
